import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Megaphone, Package } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { GlassCard, PillNavigation, Modal, Button } from '../components/ui';
import { useAfterHours } from '../hooks/useAfterHours';

// Website issues no longer get their own tab — they're reported via the IT
// Support form's "Website Issue" request type (see ITSupportForm).
type Category = 'it' | 'marketing' | 'shipping';

export function HomePage() {
  const [activeCategory, setActiveCategory] = useState<Category>('it');
  const { isAfterHours, showModal, dismissModal } = useAfterHours();

  const navItems = [
    { label: 'IT Support', mobileLabel: 'IT', value: 'it', icon: <Monitor className="w-5 h-5" /> },
    {
      label: 'Marketing',
      mobileLabel: 'Marketing',
      value: 'marketing',
      icon: <Megaphone className="w-5 h-5" />,
    },
    {
      label: 'Request to Ship',
      mobileLabel: 'Ship',
      value: 'shipping',
      icon: <Package className="w-5 h-5" />,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col w-full min-w-0 overflow-x-hidden">
      <Header />

      <main className="flex-1 w-full min-w-0 overflow-x-hidden py-8 pb-16">
        <div className="w-full max-w-4xl mx-auto px-4 box-border overflow-x-hidden">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              SMA Tickets Portal
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Submit IT support requests, marketing project requests, or warehouse
              shipping requests. Select a category below to get started.
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <PillNavigation
              items={navItems}
              activeItem={activeCategory}
              onChange={(value) => setActiveCategory(value as Category)}
            />
          </div>

          <div className="w-full min-w-0 overflow-x-hidden">
            {activeCategory === 'it' && (
              <ITSupportSection isAfterHours={isAfterHours} />
            )}
            {activeCategory === 'marketing' && <MarketingSection />}
            {activeCategory === 'shipping' && <ShippingSection />}
          </div>
        </div>
      </main>

      <Footer />

      <Modal
        isOpen={showModal}
        onClose={dismissModal}
        title="After Hours Notice"
        footer={<Button onClick={dismissModal}>I Understand</Button>}
      >
        <div className="space-y-4 text-gray-700">
          <p>
            You are accessing the support portal outside of regular business
            hours (9:00 AM – 5:00 PM ET, Monday – Friday).
          </p>
          <p>
            IT support requests submitted after hours will be addressed on the
            next business day.
          </p>
          <p className="text-gray-500 text-sm">
            Current time zone: Eastern Time (ET)
          </p>
        </div>
      </Modal>
    </div>
  );
}

interface ITSupportSectionProps {
  isAfterHours: boolean;
}

function ITSupportSection({ isAfterHours }: ITSupportSectionProps) {
  return (
    <div className="space-y-6">
      {isAfterHours && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-center">
          ⚠️ After hours — IT requests will be addressed next business day
        </div>
      )}

      <Link to="/it-support">
        <GlassCard className="p-8 text-center shadow-none">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Monitor className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              IT Support Request
            </h2>
            <p className="text-gray-600 max-w-md">
              Submit a request for IT assistance including hardware issues,
              software problems, access requests, and general technical support.
            </p>
            <span className="text-primary font-medium">Get Started →</span>
          </div>
        </GlassCard>
      </Link>

      <div className="text-center text-gray-600 text-sm mt-4">
        <p>
          Locked out of your account?{' '}
          <a
            href="mailto:helpdesk@starmicronics.com?subject=Account%20Lockout%20Assistance"
            className="text-primary hover:underline"
          >
            Email IT directly
          </a>
        </p>
      </div>
    </div>
  );
}

function MarketingSection() {
  return (
    <div className="space-y-6">
      <Link to="/marketing">
        <GlassCard className="p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Megaphone className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              Marketing Request
            </h2>
            <p className="text-gray-600 max-w-md">
              Request design assets, creative materials, trade show support,
              promotional items, and other marketing needs — all in one place.
            </p>
            <span className="text-primary font-medium">Get Started →</span>
          </div>
        </GlassCard>
      </Link>
    </div>
  );
}

function ShippingSection() {
  return (
    <div className="space-y-6">
      <Link to="/ship-request">
        <GlassCard className="p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              Request to Ship
            </h2>
            <p className="text-gray-600 max-w-md">
              Submit a shipping request to the NJ Warehouse. Specify the recipient,
              carrier, priority, and any special instructions.
            </p>
            <span className="text-primary font-medium">Start Request →</span>
          </div>
        </GlassCard>
      </Link>
    </div>
  );
}
